import React, { useCallback, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { ClassType } from '../../../api';
import useSession, { refreshSession } from '../../Util/useSession';
import labelTasksService, { LabelTaskInput } from '../../../services/labelTasks';
import styled from 'styled-components';
import { Coordinate, VideoOverlayObject, Video } from '../../VideoPlayer/VideoTypes';
import GoBackButton from './Buttons/GoBackButton';
import VideoLayout, { PanMethod } from '../../VideoPlayer/VideoLayout';
import VideoControls from '../../VideoPlayer/VideoControls';
import SessionTimeline from '../Timeline/SessionTimeline';
import { makeStyles, Button, Select, MenuItem, Tooltip, IconButton } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { fetchPost } from '../../Util/fetchAPI';
import { projection } from '../../Util/calib';
import ArrowDropDownCircleSharpIcon from '@material-ui/icons/ArrowDropDownCircleSharp';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import { secondsToHms } from '../../Util/TimeInputField';
import { Period } from '../../../services/sessionModels';

const useStyles = makeStyles(() => ({
  flexContainer: {
    display: 'flex',
  },
  sidebar: {
    width: '320px',
    minWidth: '320px',
    height: '60vh',
    overflowY: 'scroll',
    paddingRight: '32px',
  },
  sidebarRow: {
    padding: '8px 0px',
  },
  videoContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80%',
    height: '60vh',
  },
  videoControlsContainer: {
    marginTop: '12px',
    width: '320px',
    minWidth: '320px',
    height: '12vh',
    minHeight: '100px',
    backgroundColor: '#ccf1',
    border: '1px dashed #000',
  },
  timelineContainer: {
    marginTop: '12px',
    width: '80%',
    height: '12vh',
  },
  selectBox: {
    fontSize: '12px',
  },
  icon: {
    display: 'inline',
    color: '#000',
  },
  startIcon: {
    display: 'inline',
    color: '#090',
  },
  endIcon: {
    display: 'inline',
    color: '#900',
  },
  row: {
    margin: '6px 0px',
  },
}));

type TParams = {
  session: string;
};

interface Props extends RouteComponentProps<TParams> {}

const SessionAddLabelTask = (props: Props) => {
  const classes = useStyles();
  let history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  
  const sessionId: string = props.match.params.session;
  const { session } = useSession(sessionId);
  const fps: number = (session?.videos && session?.videos.length > 0 && session?.videos[0].fps) || 25;
  
  const [videoPlaying, setVideoPlaying] = useState<boolean>(false);
  const [playTime, setPlayTime] = useState<number>(0);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [timeStep, setTimeStep] = useState<number>(1);
  const [videoDuration, setVideoDuration] = useState<number>(0);

  const [startTime, setStartTime] = useState<number | undefined>(undefined);
  const [endTime, setEndTime] = useState<number | undefined>(undefined);
  const [coord, setCoord] = useState<Coordinate | undefined>(undefined);
  const [classType, setClassType] = useState<ClassType | undefined>(undefined);

  const gotoOverview = useCallback(() => {
    history.push('/session/label/' + sessionId + "/");
  }, [history, sessionId]);

  const addTask = useCallback(() => {
    if(!session || coord === undefined || classType === undefined || startTime === undefined || endTime === undefined) {
      enqueueSnackbar('Incomplete data for adding a task.', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    // Start time must be starting inside a period, so not in halftime or before/after game.
    let startTimeInPeriods: boolean = false;
    let endTimeInPeriods: boolean = false;
    session.periods?.forEach((p: Period) => {
      const periodStart: number = p.startTimeInMilliseconds || 0;
      const periodEnd: number = p.endTimeInMilliseconds || 0;
      if(periodStart <= startTime*1000 && periodEnd > startTime*1000) {
        startTimeInPeriods = true;
      }
      if(periodStart < endTime*1000 && periodEnd >= endTime*1000) {
        endTimeInPeriods = true;
      }
    });
    if(!startTimeInPeriods) {
      enqueueSnackbar('Start time must be within a period.', { variant: 'error', autoHideDuration: 3000 });
      return;
    }
    if(!endTimeInPeriods) {
      enqueueSnackbar('End time must be within a period.', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    const input: LabelTaskInput = {
      startFrame: Math.round(startTime * fps),
      endFrame: Math.round(Math.min(endTime, videoDuration) * fps),
      startX: coord.x || 0,
      startY: coord.y || 0,
      startZ: coord.z || 0,
      classType: classType,
      lineupEntryId: null,
    };

    labelTasksService.addLabelTask(sessionId, input).then(() => {
      refreshSession(sessionId);
      enqueueSnackbar('Successfully added new label task!', { variant: 'info', autoHideDuration: 3000 });
      gotoOverview();
    });
  }, [classType, coord, endTime, enqueueSnackbar, fps, gotoOverview, session, sessionId, startTime, videoDuration]);

  const absoluteSeek = useCallback(
    (newTime: number) => {
      setPlayTime((prev) => {
        // Clamp time to allowed values
        const clampedTime = Math.max(0, Math.min(newTime, videoDuration));
        return clampedTime;
      });
    },
    [videoDuration]
  );

  const relativeSeek = useCallback(
    (timeDiff: number) => {
      setPlayTime((prev) => {
        // Clamp time to allowed values
        const clampedTime = Math.max(0, Math.min(prev + timeDiff, videoDuration));
        return clampedTime;
      });
    },
    [videoDuration]
  );

  const onChangeVideoDuration = useCallback((duration: number) => {
    setVideoDuration(duration);
  }, []);
  
  const reprojectCoord = useCallback(
    (videoId: string, coordinate: Coordinate) => {
      if(startTime !== playTime) return;
      
      const video: Video | undefined | null = session?.videos?.find((vid: Video) => {
        return String(vid.id) === videoId;
      });
      if (!video) return;

      const calib = JSON.parse(video?.calibration || '');

      //get the 3d position from clicked point
      fetchPost('/gateway/calibrations/reprojection', {
        calibration: calib,
        camera_coordinates: [[coordinate.x, coordinate.y]],
      }).then((data: number[][]) => {
        const result = projection(calib, data);
        const invalidResult: boolean = result[0][0] === -1;
        if(invalidResult) {
          enqueueSnackbar('Point is invalid.', { variant: 'error', autoHideDuration: 3000 });
          return;
        }
        setCoord({
          x: data[0][0],
          y: data[0][1],
          z: 0,
        });
      });
    },
    [startTime, playTime, session, enqueueSnackbar]
  );

  const overlayItems = new Map<string, VideoOverlayObject[]>();
  if(coord !== undefined && startTime === playTime) {
    session?.videos?.forEach((video: Video) => {
      const calib = JSON.parse(video?.calibration || '');
      const result = projection(calib, [[coord?.x, coord?.y, 0]]);
      const projectedCoord: Coordinate | undefined = {
        x: result[0][0],
        y: result[0][1],
        z: 0,
      };
      if (!projectedCoord) return;

      const viewOverlayObject: VideoOverlayObject = {
        svg: (
          <svg viewBox="0 0 14 14">
            <circle
              cx={7}
              cy={7}
              r={5}
              fill={"#fff"}
              stroke={'#000'}
              strokeWidth={2}
            />
          </svg>
        ),
        width: 14,
        height: 14,
        center: projectedCoord,
      };

      overlayItems.set(String(video.id), [viewOverlayObject]);
    });
  }

  const mouseOverlay: VideoOverlayObject | undefined = startTime === playTime ? {
    svg: <circle cx={5} cy={5} r={5} fill={"#090"} stroke="#000" strokeWidth={1} />,
    width: 10,
    height: 10,
    center: { x: 0, y: 0 },
  } : undefined;

  return (
    <Container className="sciui-card sciui-m-l sciui-p-l sciui-bg-p-5">
      <Wrapper>

      <div className={classes.flexContainer}>
        <div className={classes.sidebar}>
          <div className={classes.sidebarRow}>

            <GoBackButton path={'/session/label/' + sessionId} />

            <div className={classes.row}>
              <b>New task</b>
            </div>

            <div className={classes.row}>
              Start time:
              <Tooltip title="Set current video time as start time" arrow>
                <IconButton
                  className={classes.startIcon}
                  size="small"
                  onClick={() => {
                    setStartTime(playTime);
                  }}
                >
                  <ArrowDropDownCircleSharpIcon />
                </IconButton>
              </Tooltip>
              {startTime && secondsToHms(startTime)}
              {startTime &&
                <Tooltip title="Go to start time" arrow>
                  <IconButton
                    className={classes.icon}
                    size="small"
                    onClick={() => {
                      absoluteSeek(startTime);
                    }}
                  >
                    <PlayCircleFilledIcon />
                  </IconButton>
                </Tooltip>
              }
            </div>

            <div className={classes.row}>
              End time:
              <Tooltip title="Set current video time as end time" arrow>
                <IconButton
                  className={classes.endIcon}
                  size="small"
                  onClick={() => {
                    setEndTime(playTime);
                  }}
                >
                  <ArrowDropDownCircleSharpIcon />
                </IconButton>
              </Tooltip>
              {endTime && secondsToHms(endTime)}
              {endTime &&
                <Tooltip title="Go to end time" arrow>
                  <IconButton
                    className={classes.icon}
                    size="small"
                    onClick={() => {
                      absoluteSeek(endTime);
                    }}
                  >
                    <PlayCircleFilledIcon />
                  </IconButton>
                </Tooltip>
              }
            </div>

            <div className={classes.row}>
              Type: &nbsp;
              <Select
                className={classes.selectBox}
                value={classType}
                onChange={(e) => setClassType(e.target.value as ClassType)}
              >
                <MenuItem value={undefined}></MenuItem>
                <MenuItem value={ClassType.Ball}>Ball</MenuItem>
                <MenuItem value={ClassType.HomeKeeper}>Goalkeeper {session?.homeTeam?.name || 'home'}</MenuItem>
                <MenuItem value={ClassType.HomePlayer}>Player {session?.homeTeam?.name || 'home'}</MenuItem>
                <MenuItem value={ClassType.AwayKeeper}>Goalkeeper {session?.awayTeam?.name || 'away'}</MenuItem>
                <MenuItem value={ClassType.AwayPlayer}>Player {session?.awayTeam?.name || 'away'}</MenuItem>
              </Select>
            </div>

            <div className={classes.row}>
              Start position: {coord ? <>
                  <div>x: {coord.x.toFixed(2)}</div>
                  <div>y: {coord.y.toFixed(2)}</div>
                  <div>z: {(coord?.z||0).toFixed(2)}</div>
                </> 
              : startTime === undefined ? "<insert start time first>" : "<click in view>"}
            </div>

            <div className={classes.row}>
              <Button
                variant="contained"
                color="primary"
                onClick={addTask}
                disabled={
                  classType === undefined 
                  || coord === undefined 
                  || startTime === undefined 
                  || endTime === undefined
                  || endTime <= startTime
                }
              >
                Add new task
              </Button>
            </div>

          </div>
        </div>

        <div className={classes.videoContainer}>
          <VideoLayout
            videos={session?.videos || []}
            showOverlayUI={false}
            updateIntervalMs={40}
            videoPlaying={videoPlaying}
            onChangeVideoPlaying={setVideoPlaying}
            playTime={playTime}
            playTimeChanged={setPlayTime}
            playbackSpeed={playbackSpeed}
            onChangeVideoDuration={onChangeVideoDuration}
            noFullscreen
            overridePanMethod={PanMethod.Drag}
            onViewMouseDown={reprojectCoord}
            viewOverlays={overlayItems}
            mouseOverlay={mouseOverlay ? [mouseOverlay] : []}
          />
        </div>
      </div>
      <div className={classes.flexContainer}>
        <div className={classes.videoControlsContainer}>
          <VideoControls
            isPlaying={videoPlaying}
            onChangeVideoPlaying={setVideoPlaying}
            relativeSeek={relativeSeek}
            playbackSpeed={playbackSpeed}
            setPlaybackSpeed={setPlaybackSpeed}
            timeStep={timeStep}
            setTimeStep={setTimeStep}
          />
        </div>
        <div className={classes.timelineContainer}>
          <SessionTimeline
            playTime={playTime}
            seekToPlayTime={absoluteSeek}
            videoDuration={videoDuration}
            teamHome={session?.homeTeam}
            teamAway={session?.awayTeam}
            periods={session?.periods || []}
          />
        </div>
      </div>
      </Wrapper>
    </Container>
  );
};

const Container = styled.div`
  min-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px;
`;

export default SessionAddLabelTask;